import moment from "moment";
import { validateRecurrenceEndAfterBlockEnd } from "../../../../utils/timeUtils";

/**
 * The shared Repeat rule (design handoff "Nested: Repeat"; prototype Repeat
 * Editor), mapped onto the existing time-off API (`recurrence_interval`,
 * `recurrence_unit` Day / Week / Month, `recurrence_end_date`). Pure.
 *
 * - Presets: Does not repeat · Every 4 weeks · Every 3 months · Every 6
 *   months · Custom ("Every [n] [days | weeks | months]"; Day kept, Year
 *   dropped, decided 2026-10-09).
 * - Ends (prototype): After [n] times, or On a date. Hard cap: 5 years from
 *   the start; the date is clamped and the count is clamped to fit.
 */

export type RepeatPreset = "none" | "w4" | "m3" | "m6" | "custom";
export type RepeatUnit = "Day" | "Week" | "Month";
export type RepeatEnds = "after" | "on";

export interface RepeatRule {
  preset: RepeatPreset;
  /** Custom only. */
  unit: RepeatUnit;
  interval: number;
  endsType: RepeatEnds;
  /** "YYYY-MM-DD" (ends on). */
  until: string;
  /** Occurrences, including the first (ends after). */
  count: number;
}

export const REPEAT_PRESETS: { value: RepeatPreset; label: string }[] = [
  { value: "none", label: "Does not repeat" },
  { value: "w4", label: "Every 4 weeks" },
  { value: "m3", label: "Every 3 months" },
  { value: "m6", label: "Every 6 months" },
  { value: "custom", label: "Custom" },
];

export const MAX_INTERVAL: Record<RepeatUnit, number> = {
  Day: 365,
  Week: 52,
  Month: 24,
};

/** 5 years, in days (prototype horizon). */
export const HORIZON_DAYS = 1826;

const DAY = "YYYY-MM-DD";

export const NO_REPEAT = (startDay: string): RepeatRule => ({
  preset: "none",
  unit: "Week",
  interval: 1,
  endsType: "after",
  until: moment(startDay, DAY).add(3, "months").format(DAY),
  count: 6,
});

/** The rule's effective step (interval + unit), or null when not repeating. */
export const ruleStep = (
  r: RepeatRule,
): { interval: number; unit: RepeatUnit } | null => {
  switch (r.preset) {
    case "w4":
      return { interval: 4, unit: "Week" };
    case "m3":
      return { interval: 3, unit: "Month" };
    case "m6":
      return { interval: 6, unit: "Month" };
    case "custom":
      return {
        interval: Math.max(1, Math.min(MAX_INTERVAL[r.unit], r.interval || 1)),
        unit: r.unit,
      };
    default:
      return null;
  }
};

const unitKey = (u: RepeatUnit) =>
  u === "Day" ? "days" : u === "Week" ? "weeks" : "months";

export const maxUntil = (startDay: string): string =>
  moment(startDay, DAY).add(HORIZON_DAYS, "days").format(DAY);

/** Earliest "ends on" date: the day after the block's end day (the backend
 * needs the repeat to end after the block does). */
export const minUntil = (endDay: string): string =>
  moment(endDay, DAY).add(1, "day").format(DAY);

/** Most occurrences that fit in 5 years for this rule. */
export const maxCount = (r: RepeatRule, startDay: string): number => {
  const step = ruleStep(r);
  if (!step) return 1;
  const limit = moment(startDay, DAY).add(HORIZON_DAYS, "days");
  let n = 1;
  const at = moment(startDay, DAY);
  for (;;) {
    at.add(step.interval, unitKey(step.unit));
    if (at.isAfter(limit) || n >= 2000) return n;
    n += 1;
  }
};

/** The last occurrence's day ("YYYY-MM-DD"), capped at 5 years. */
export const ruleEndDay = (r: RepeatRule, startDay: string): string | null => {
  const step = ruleStep(r);
  if (!step) return null;
  const cap = maxUntil(startDay);
  if (r.endsType === "on") return r.until > cap ? cap : r.until;
  const count = Math.max(2, Math.min(maxCount(r, startDay), r.count || 2));
  return moment(startDay, DAY)
    .add(step.interval * (count - 1), unitKey(step.unit))
    .format(DAY);
};

/**
 * The rule's last occurrence must be after the block's end day (the backend
 * rejects "recurrence_end_date must be after end_dt"). The error message, or
 * undefined when valid / not repeating. Shared by the Repeat screen's Done
 * and the time block form's submit.
 */
export const repeatEndError = (
  r: RepeatRule,
  startDay: string,
  endDay: string,
): string | undefined => {
  const end = ruleEndDay(r, startDay);
  if (!end) return undefined;
  return validateRecurrenceEndAfterBlockEnd(end, endDay).recurrenceEndDate;
};

/** Every occurrence day of the rule (first included), capped at 5 years. */
export const occurrenceDays = (r: RepeatRule, startDay: string): string[] => {
  const step = ruleStep(r);
  const end = ruleEndDay(r, startDay);
  if (!step || !end) return [startDay];
  const out: string[] = [];
  const at = moment(startDay, DAY);
  while (at.format(DAY) <= end && out.length < 2000) {
    out.push(at.format(DAY));
    at.add(step.interval, unitKey(step.unit));
  }
  return out;
};

/** The create payload's recurrence fields (empty when not repeating). */
export const rulePayload = (
  r: RepeatRule,
  startDay: string,
): {
  recurrence_interval?: number;
  recurrence_unit?: RepeatUnit;
  recurrence_end_date?: string;
} => {
  const step = ruleStep(r);
  const end = ruleEndDay(r, startDay);
  if (!step || !end) return {};
  return {
    recurrence_interval: step.interval,
    recurrence_unit: step.unit,
    recurrence_end_date: end,
  };
};

const plural = (n: number, unit: RepeatUnit) =>
  `${n} ${unit.toLowerCase()}${n === 1 ? "" : "s"}`;

/** "Every 4 weeks, 6 times" / "Every 2 days, until Mar 1, 2027" / "Does not repeat". */
export const ruleText = (r: RepeatRule, startDay: string): string => {
  const step = ruleStep(r);
  if (!step) return "Does not repeat";
  const every =
    step.interval === 1
      ? `Every ${step.unit.toLowerCase()}`
      : `Every ${plural(step.interval, step.unit)}`;
  if (r.endsType === "after") {
    const n = occurrenceDays(r, startDay).length;
    return `${every}, ${n} times`;
  }
  const end = ruleEndDay(r, startDay)!;
  return `${every}, until ${moment(end, DAY).format("MMM D, YYYY")}`;
};

/** An existing series' rule text from its stored interval / unit / end date. */
export const seriesText = (
  interval: number | undefined,
  unit: string | undefined,
  endDate: string | undefined,
): string => {
  if (!interval || !unit) return "Does not repeat";
  const u = (
    ["Day", "Week", "Month"].includes(unit) ? unit : "Day"
  ) as RepeatUnit;
  const label =
    unit === "Year"
      ? `Every ${interval === 1 ? "year" : `${interval} years`}`
      : interval === 1
        ? `Every ${u.toLowerCase()}`
        : `Every ${plural(interval, u)}`;
  return endDate
    ? `${label}, until ${moment(endDate).format("MMM D, YYYY")}`
    : label;
};
